import { useState, useEffect, useCallback } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { AffiliatesService, type AffiliateDetailRecord } from '@/services/affiliates'
import { AddReferralModal } from './components/AddReferralModal'
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '@/components/ui/table'
import { TableSkeleton } from '@/components/skeletons'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { BILLING_STATUS_CONFIG, billingKeyFromStripeStatus } from '@/lib/billingStatus'
import {
    Tooltip,
    TooltipContent,
    TooltipTrigger,
} from '@/components/ui/tooltip'
import { Button } from '@/components/ui/button'
import { ArrowLeft, Link2, Users, CreditCard, TrendingUp, PieChart, UserPlus } from 'lucide-react'
import { Skeleton } from '@/components/ui/skeleton'
import { RelativeTime } from '@/components/RelativeTime'
import { TruncatedValue } from '@/components/TruncatedValue'

// Status badge config — same as AgencyDetail
function getStatusBadge(stripeStatus: string | null) {
    const key = billingKeyFromStripeStatus(stripeStatus)
    const config = BILLING_STATUS_CONFIG[key] || BILLING_STATUS_CONFIG.free
    return (
        <Tooltip>
            <TooltipTrigger className="cursor-default">
                <Badge tone={config.tone}>
                    {config.label}
                </Badge>
            </TooltipTrigger>
            <TooltipContent>
                <p>{config.tooltip}</p>
            </TooltipContent>
        </Tooltip>
    )
}

function AffiliateDetailSkeleton() {
    return (
        <div className="space-y-6 w-full animate-in fade-in duration-300 pb-12">
            <div className="flex items-center gap-3">
                <Skeleton className="h-9 w-9 rounded-md" />
                <Skeleton className="h-8 w-48" />
            </div>
            <div className="grid grid-cols-1 xs:grid-cols-2 xl:grid-cols-4 gap-4">
                {Array.from({ length: 4 }).map((_, i) => (
                    <Card key={i} className="py-0">
                        <CardContent className="p-4">
                            <div className="flex items-center gap-4">
                                <Skeleton className="h-12 w-12 rounded-lg" />
                                <div className="flex flex-col gap-2 flex-1">
                                    <Skeleton className="h-7 w-16" />
                                    <Skeleton className="h-4 w-24" />
                                </div>
                            </div>
                        </CardContent>
                    </Card>
                ))}
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* Left Column Skeleton */}
                <div className="lg:col-span-1 space-y-6">
                    <Card className="py-0 gap-0 shadow-sm">
                        <CardHeader className="px-5 py-4 gap-0 border-b">
                            <Skeleton className="h-5 w-40" />
                            <Skeleton className="h-4 w-48 mt-1.5" />
                        </CardHeader>
                        <CardContent className="p-5">
                            <div className="space-y-4">
                                <div className="flex justify-between">
                                    <Skeleton className="h-4 w-12" />
                                    <Skeleton className="h-4 w-48" />
                                </div>
                            </div>
                        </CardContent>
                    </Card>
                </div>

                {/* Right Column Skeleton */}
                <div className="lg:col-span-2">
                    <Card className="py-0 gap-0 shadow-sm">
                        <CardHeader className="px-5 py-4 gap-0 border-b">
                            <Skeleton className="h-5 w-36" />
                            <Skeleton className="h-4 w-56 mt-1.5" />
                        </CardHeader>
                        <CardContent className="p-0">
                            <TableSkeleton columns={COLUMN_COUNT} rows={5} />
                        </CardContent>
                    </Card>
                </div>
            </div>
        </div>
    )
}

const COLUMN_COUNT = 5

export default function AffiliateDetail() {
    const { id } = useParams<{ id: string }>()
    const navigate = useNavigate()

    const [affiliate, setAffiliate] = useState<AffiliateDetailRecord | null>(null)
    const [isLoading, setIsLoading] = useState(true)
    const [addReferralOpen, setAddReferralOpen] = useState(false)

    const fetchAffiliate = useCallback(async (showSkeleton = true) => {
        if (!id) return
        if (showSkeleton) setIsLoading(true)
        try {
            const data = await AffiliatesService.getById(id)
            setAffiliate(data)
        } catch (error) {
            console.error('Failed to load affiliate details', error)
        } finally {
            if (showSkeleton) setIsLoading(false)
        }
    }, [id])

    useEffect(() => {
        fetchAffiliate()
    }, [fetchAffiliate])

    if (isLoading || !affiliate) {
        return <AffiliateDetailSkeleton />
    }

    const statsCards = [
        {
            title: 'Referred',
            value: affiliate.referredCount.toLocaleString(),
            icon: Users,
            description: 'Agencies referred',
            iconBg: 'bg-indigo-100 dark:bg-indigo-950/50',
            iconColor: 'text-indigo-600 dark:text-indigo-400',
        },
        {
            title: 'Converted',
            value: affiliate.convertedCount.toLocaleString(),
            icon: TrendingUp,
            description: 'Converted to paid',
            iconBg: 'bg-emerald-100 dark:bg-emerald-950/50',
            iconColor: 'text-emerald-600 dark:text-emerald-400',
        },
        {
            title: 'MRR Contribution',
            value: `$${affiliate.mrrContribution.toLocaleString()}`,
            icon: CreditCard,
            description: 'Monthly revenue',
            iconBg: 'bg-amber-100 dark:bg-amber-950/50',
            iconColor: 'text-amber-600 dark:text-amber-400',
        },
        {
            title: 'Conv. Rate',
            value: `${affiliate.conversionRate}%`,
            icon: PieChart,
            description: 'Conversion rate',
            iconBg: 'bg-violet-100 dark:bg-violet-950/50',
            iconColor: 'text-violet-600 dark:text-violet-400',
        },
    ]

    return (
        <div className="space-y-6 w-full animate-in fade-in duration-300 pb-12">
            {/* Header */}
            <div className="flex items-center gap-3">
                <Button variant="outline" size="icon" onClick={() => navigate('/affiliates')} className="h-9 w-9">
                    <ArrowLeft className="h-4 w-4" />
                </Button>
                <h1 className="text-2xl sm:text-3xl font-bold tracking-tight flex-1 min-w-0 truncate">{affiliate.name}</h1>
                <Button onClick={() => setAddReferralOpen(true)}>
                    <UserPlus className="h-4 w-4 mr-2" />
                    Add Referral
                </Button>
            </div>

            {/* Stats Cards */}
            <div className="grid grid-cols-1 xs:grid-cols-2 xl:grid-cols-4 gap-4">
                {statsCards.map((stat, index) => (
                    <Card
                        key={stat.title}
                        className="py-0 animate-in fade-in duration-300"
                        style={{ animationDelay: `${index * 50}ms` }}
                    >
                        <CardContent className="p-4">
                            <div className="flex items-center gap-4">
                                <div className={`flex h-12 w-12 items-center justify-center rounded-lg shrink-0 ${stat.iconBg} ${stat.iconColor}`}>
                                    <stat.icon className="h-6 w-6" />
                                </div>
                                <div className="flex flex-col min-w-0">
                                    <span className="text-2xl font-bold text-foreground leading-tight">
                                        {stat.value}
                                    </span>
                                    <span className="text-sm text-muted-foreground truncate">
                                        {stat.title}
                                    </span>
                                </div>
                            </div>
                        </CardContent>
                    </Card>
                ))}
            </div>

            {/* Two-Column Layout */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                
                {/* Left Column: Affiliate Info Card */}
                <div className="lg:col-span-1 space-y-6">
                    <Card className="py-0 gap-0 shadow-sm">
                        <CardHeader className="px-5 py-4 gap-0 border-b">
                            <CardTitle className="text-base">Affiliate Information</CardTitle>
                            <CardDescription>Contact and referral details</CardDescription>
                        </CardHeader>
                        <CardContent className="p-5">
                            <div className="space-y-4 text-sm">
                                <div className="flex justify-between items-center gap-4">
                                    <span className="text-muted-foreground shrink-0">Email</span>
                                    <span className="font-medium text-right truncate max-w-[65%]">
                                        {affiliate.email || '—'}
                                    </span>
                                </div>
                                <div className="flex justify-between items-center gap-4">
                                    <span className="text-muted-foreground shrink-0">Affiliate ID</span>
                                    <TruncatedValue value={affiliate.id} className="font-mono" />
                                </div>
                                <div className="flex justify-between items-center gap-4">
                                    <span className="text-muted-foreground shrink-0">Referral code</span>
                                    <TruncatedValue value={affiliate.trackingId || '—'} className="font-mono" />
                                </div>
                            </div>
                        </CardContent>
                    </Card>
                </div>

                {/* Right Column: Referred Agencies Table */}
                <div className="lg:col-span-2">
                    <Card className="py-0 gap-0 shadow-sm">
                        <CardHeader className="px-5 py-4 gap-0 border-b">
                            <CardTitle className="text-base">
                                Referred Agencies {affiliate.referrals.length > 0 && `(${affiliate.referrals.length})`}
                            </CardTitle>
                            <CardDescription>Agencies referred by this affiliate</CardDescription>
                        </CardHeader>
                        <CardContent className="p-0">
                            <div className="overflow-x-auto">
                                <Table>
                                    <TableHeader>
                                        <TableRow className="bg-muted/50 border-t-0">
                                            <TableHead className="min-w-[200px]">Agency</TableHead>
                                            <TableHead className="text-center min-w-[180px]">Super Admin</TableHead>
                                            <TableHead className="text-center w-[140px]">Plan</TableHead>
                                            <TableHead className="text-center w-[120px]">Created At</TableHead>
                                            <TableHead className="text-center w-[120px]">Status</TableHead>
                                        </TableRow>
                                    </TableHeader>
                                    <TableBody>
                                        {affiliate.referrals.length === 0 ? (
                                            <TableRow>
                                                <TableCell colSpan={COLUMN_COUNT} className="h-48 text-center text-muted-foreground">
                                                    <div className="flex flex-col items-center justify-center">
                                                        <div className="mx-auto w-12 h-12 rounded-full bg-muted flex items-center justify-center mb-4">
                                                            <Link2 className="h-6 w-6 text-muted-foreground" />
                                                        </div>
                                                        <h3 className="text-lg font-medium mb-2">No referred agencies</h3>
                                                        <p className="text-sm text-muted-foreground">
                                                            No agencies have been matched to this affiliate yet.
                                                        </p>
                                                    </div>
                                                </TableCell>
                                            </TableRow>
                                        ) : (
                                            affiliate.referrals.map((ref) => (
                                                <TableRow
                                                    key={ref.agencyId}
                                                    className="cursor-pointer hover:bg-muted/50 transition-colors"
                                                    onClick={() => navigate(`/agencies/${ref.agencyId}`)}
                                                >
                                                    <TableCell>
                                                        <span className="font-medium">{ref.agencyName}</span>
                                                    </TableCell>
                                                    <TableCell className="text-center">
                                                        <div className="flex flex-col min-w-0 items-center">
                                                            <span className="font-medium text-sm">{ref.ownerName}</span>
                                                            <span className="text-xs text-muted-foreground">{ref.ownerEmail}</span>
                                                        </div>
                                                    </TableCell>
                                                    <TableCell className="text-center">
                                                        {ref.planName ? (
                                                            <span className="font-medium text-sm">{ref.planName}</span>
                                                        ) : (
                                                            <span className="text-muted-foreground text-sm">Not selected</span>
                                                        )}
                                                    </TableCell>
                                                    <TableCell className="text-center text-muted-foreground">
                                                        <RelativeTime date={ref.signedUpAt} className="cursor-default" />
                                                    </TableCell>
                                                    <TableCell className="text-center">
                                                        {getStatusBadge(ref.status)}
                                                    </TableCell>
                                                </TableRow>
                                            ))
                                        )}
                                    </TableBody>
                                </Table>
                            </div>
                        </CardContent>
                    </Card>
                </div>
            </div>

            {id && (
                <AddReferralModal
                    affiliateId={id}
                    affiliateName={affiliate.name}
                    open={addReferralOpen}
                    onOpenChange={setAddReferralOpen}
                    onSuccess={() => fetchAffiliate(false)}
                />
            )}
        </div>
    )
}
